import { useEffect, useRef, useState, type FormEvent } from 'react'
import { PencilLine } from 'lucide-react'
import { Dialog } from '@components/Dialog.tsx'
import { useRouter } from '@router'
import styles from './AddRecipeDialog.module.css'

type Props = {
	open: boolean
	onClose: () => void
	// Fire-and-forget: the caller shows an optimistic pending card and
	// ingests in the background, so we close as soon as it's handed off.
	onSubmit: (url: string) => void
}

// Recipe-add dialog: paste a URL to scrape, or head to the manual create page.
export const AddRecipeDialog = ({ open, onClose, onSubmit }: Props) => {
	const { navigate } = useRouter()
	const inputRef = useRef<HTMLInputElement>(null)
	const [url, setUrl] = useState('')

	useEffect(() => {
		if (open) {
			setUrl('')
			queueMicrotask(() => inputRef.current?.focus())
		}
	}, [open])

	const submit = (e: FormEvent) => {
		e.preventDefault()
		const trimmed = url.trim()
		if (trimmed.length === 0) return
		onSubmit(trimmed)
		onClose()
	}

	return (
		<Dialog open={open} onClose={onClose}>
			<form className={styles.panel} onSubmit={submit}>
				<h2 className={styles.heading}>Add a recipe</h2>

				<label className={styles.field}>
					<span className={styles.label}>Recipe URL</span>
					<input
						ref={inputRef}
						className={styles.input}
						type="url"
						value={url}
						placeholder="Paste a recipe URL…"
						onChange={(e) => setUrl(e.target.value)}
					/>
				</label>

				<div className={styles.actions}>
					<button
						type="button"
						className={styles.cancel}
						onClick={onClose}
					>
						Cancel
					</button>
					<button
						type="submit"
						className={styles.submit}
						disabled={url.trim().length === 0}
					>
						Add
					</button>
				</div>

				<div className={styles.divider}>
					<span>or</span>
				</div>

				<button
					type="button"
					className={styles.writeOwn}
					onClick={() => {
						onClose()
						navigate('/recipes/new')
					}}
				>
					<PencilLine size={16} aria-hidden="true" />
					Write your own
				</button>
			</form>
		</Dialog>
	)
}
